import React from 'react';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import SearchIcon from '@material-ui/icons/Search';
import AsyncSelect from 'react-select/async';
import { useSelectDebounce } from 'utils/react';
import { Typography } from '@material-ui/core';
import { naturalSort } from 'utils/sorting';

const useStyles = makeStyles(theme => ({
    row: {
        display: 'flex',
        margin: `${theme.spacing(2)}px 0px`,
    },
    cell: {
        flexGrow: 1,
        width: '50%',
        display: 'flex',
        alignItems: 'center',
    },
    cellBlock: {
        display: 'block',
    },
    searchIcon: {
        width: 36,
        color: 'hsl(0,0%,80%)',
        '&:hover': {
            color: 'hsl(0,0%,60%)',
        },
    },
    searchIconActive: {
        width: 36,
        color: 'hsl(0,0%,40%)',
    },
}));

const toOptionsArr = options =>
    Object.keys(options).map(key => ({
        label: options[key],
        value: key,
    }));

export const MappingForm = ({ values, onChange, config, service, lookups }) => {
    const { serviceProvider } = config;

    const changeFactory = name => value => {
        onChange({ [name]: value });
    };

    const loadOptions = async value => {
        if (!value) {
            return [];
        }
        let result = await serviceProvider.companiesExternal(service, value);
        result = toOptionsArr(result);
        result = result.filter(item => {
            const values = Object.keys(lookups.external_company) || [];
            return !values.includes(item.value);
        });

        return naturalSort(result, item => item.label);
    };

    const loadAgreementOptions = async value => {
        if (!value) {
            return [];
        }
        let result = await serviceProvider.companiesExternalAgreements(
            service,
            values.company.value,
            value
        );
        result = toOptionsArr(result);
        result = result.filter(item => {
            const values = Object.keys(lookups.external_company) || [];
            return !values.includes(item.value);
        });

        return naturalSort(result, item => item.label);
    };

    const { setSearchTerm, setCallback } = useSelectDebounce(loadOptions);
    const {
        setSearchTerm: setAgreementSearchTerm,
        setCallback: setAgreementCallback,
    } = useSelectDebounce(loadAgreementOptions);

    const handleLoadOptions = (term, callback) => {
        setCallback(() => {
            return callback;
        });
        setSearchTerm(term);
    };

    const handleAgreementLoadOptions = (term, callback) => {
        setAgreementCallback(() => {
            return callback;
        });
        setAgreementSearchTerm(term);
    };

    return (
        <>
            <Row>
                <Cell>
                    <Typography>{config.label} Company:</Typography>
                </Cell>
                <Cell block>
                    <AsyncSelect
                        value={values.company || ''}
                        onChange={changeFactory('company')}
                        placeholder="Search..."
                        cacheOptions
                        loadOptions={loadOptions && handleLoadOptions}
                        isSearchable
                        styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                        }}
                        menuPosition="absolute"
                        noOptionsMessage={() => 'Start typing to search'}
                        menuPortalTarget={document.body}
                        components={{
                            DropdownIndicator,
                        }}
                    />
                </Cell>
            </Row>
            <Row>
                <Cell>
                    <Typography>
                        {config.label} {config.agreementLabel}:
                    </Typography>
                </Cell>
                <Cell block>
                    <AsyncSelect
                        isDisabled={!values.company}
                        value={values.agreement || ''}
                        onChange={changeFactory('agreement')}
                        placeholder="Search..."
                        cacheOptions
                        loadOptions={
                            loadAgreementOptions && handleAgreementLoadOptions
                        }
                        isSearchable
                        styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                        }}
                        menuPosition="absolute"
                        noOptionsMessage={() => 'Start typing to search'}
                        menuPortalTarget={document.body}
                        components={{
                            DropdownIndicator,
                        }}
                    />
                </Cell>
            </Row>
        </>
    );
};

const DropdownIndicator = ({ isFocused }) => {
    const classes = useStyles();
    const className = classNames({
        [classes.searchIcon]: !isFocused,
        [classes.searchIconActive]: isFocused,
    });
    return <SearchIcon className={className} />;
};

const Row = ({ children }) => {
    const classes = useStyles();
    return <div className={classes.row}>{children}</div>;
};

const Cell = ({ children, block }) => {
    const classes = useStyles();
    const className = classNames(classes.cell, {
        [classes.cellBlock]: block,
    });
    return <div className={className}>{children}</div>;
};
